import { HeadData } from '@/components/HeadData';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { useSidebar } from '@/routes/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import Page from '../-Page';
import { useCreateRolePlayFeedbackMutation } from '../-hooks/useCreateRolePlayFeedbackMutation';
import { useEvaluationPolling } from '../../-hooks/useEvaluationPolling';
import { COURSE_LESSON_DETAIL_FRAGMENT } from '../../index.route.lazy';

export const ROLE_PLAY_RESULT_FRAGMENT = graphql(/* GraphQL */ `
  fragment RolePlayResult on UserRolePlayResult {
    id
    isEvaluated
    evaluationComment
    isFirst
    passRatePercentage
    hasFeedback
    learningPoint {
      title
    }
    embaCourse {
      name
    }
    evaluationItemsWithResults {
      id
      title
      description
      firstResultEvaluationItem {
        isPassed
        positiveFeedback
        improvementFeedback
      }
      secondResultEvaluationItem {
        isPassed
        positiveFeedback
        improvementFeedback
      }
      currentResultEvaluationItem {
        isPassed
        positiveFeedback
        improvementFeedback
      }
    }
    sessionLogs {
      id
      speaker
      content
    }
    firstRolePlayResult {
      passRatePercentage
    }
    secondRolePlayResult {
      passRatePercentage
    }
  }
`);

export type RolePlayResultContainerProps = {
  rolePlayResult: FragmentType<typeof ROLE_PLAY_RESULT_FRAGMENT>;
  embaCourseLesson: FragmentType<typeof COURSE_LESSON_DETAIL_FRAGMENT>;
  lessonSlug: string;
  userAttendanceSlug: string;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
  isTimeout: boolean;
};

export default function RolePlayResultContainer({
  rolePlayResult,
  embaCourseLesson,
  lessonSlug,
  userAttendanceSlug,
  startPolling,
  stopPolling,
  isTimeout,
}: RolePlayResultContainerProps) {
  const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(isTimeout);
  const [isFeedbackCreated, setIsFeedbackCreated] = useState(false);
  const navigate = useNavigate();
  const { showSidebar } = useSidebar();
  useEffect(() => {
    showSidebar();
  }, [showSidebar]);

  const rolePlayResultData = useFragment(
    ROLE_PLAY_RESULT_FRAGMENT,
    rolePlayResult,
  );

  const lessonData = useFragment(
    COURSE_LESSON_DETAIL_FRAGMENT,
    embaCourseLesson,
  );

  useEvaluationPolling(
    rolePlayResultData.isEvaluated,
    startPolling,
    stopPolling,
  );

  const {
    createRolePlayFeedback,
    createRolePlayFeedbackMutationError,
    createRolePlayFeedbackMutationLoading,
  } = useCreateRolePlayFeedbackMutation(rolePlayResultData.id);

  if (createRolePlayFeedbackMutationError) {
    throw createRolePlayFeedbackMutationError;
  }

  const onClickNext = () => {
    navigate({
      to: '/user-attendances/$userAttendanceSlug/lessons/$lessonSlug/complete',
      params: {
        userAttendanceSlug,
        lessonSlug,
      },
    });
  };

  const onCreateRolePlayFeedback = async (rating: number, comment: string) => {
    await createRolePlayFeedback(rating, comment);
    setIsFeedbackCreated(true);
  };

  const headTitle = rolePlayResultData.embaCourse.name;

  return (
    <>
      <HeadData title={headTitle} />
      <Page
        rolePlayResult={rolePlayResultData}
        onClickNext={onClickNext}
        isTimeoutModalOpen={isTimeoutModalOpen}
        setIsTimeoutModalOpen={setIsTimeoutModalOpen}
        chapterName={lessonData.embaCourseChapter.name}
        lessonName={lessonData.name}
        onCreateRolePlayFeedback={onCreateRolePlayFeedback}
        isFeedbackCreating={createRolePlayFeedbackMutationLoading}
        isFeedbackCreated={isFeedbackCreated}
      />
    </>
  );
}
